import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import {
    useCreateLoadTemplateMutation,
    useGetAllLoadTypesQuery,
    useUpdateLoadTemplateMutation,
} from '../../app/redux-fetch/apiOrders'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import FormInput from '../../components/Forms/FormInput'
import FormSelect from '../../components/Forms/FormSelect'
import SharedPhysicalProperties from '../../components/Forms/PhysicalProperties/SharedPhysicalProperties'
import TabWrapper, { TabItem } from '../../components/TabWrapper/TabWrapper'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import messages from './messages'
import { LoadTemplateFormData, loadTemplateSchema } from './schema'
import { ILoadTemplate } from './types'

type LoadTemplateFormProps = {
    loadTemplate: ILoadTemplate
    handleClose: () => void
    isOpen: boolean
}

const LoadTemplateForm = ({ loadTemplate, handleClose, isOpen }: LoadTemplateFormProps) => {
    const { formatMessage } = useIntl()
    const isNew = !loadTemplate?.id || loadTemplate?.id === 0

    const [addLoadTemplate] = useCreateLoadTemplateMutation()
    const [updateLoadTemplate] = useUpdateLoadTemplateMutation()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()
    const { data: loadTypes = [] } = useGetAllLoadTypesQuery()

    const methods = useForm<LoadTemplateFormData>({
        resolver: zodResolver(loadTemplateSchema),
        defaultValues: loadTemplate,
    })

    const onSubmit = async (data: LoadTemplateFormData) => {
        const { customerId } = loadTemplate
        const body = { ...data, customerId }

        try {
            if (isNew) {
                await addLoadTemplate({ customerId, body }).unwrap()
                enqueueSuccess(messages.loadTemplateCreated)
            } else {
                await updateLoadTemplate({ customerId, body }).unwrap()
                enqueueSuccess(messages.loadTemplateUpdated)
            }
            handleClose()
        } catch (error) {
            if (isNew) {
                enqueueFailure(messages.loadTemplateNotAdded)
            } else {
                enqueueFailure(messages.loadTemplateNotUpdated)
            }
        }
    }

    const loadTypeOptions = loadTypes.map((type: any) => ({
        value: type.id,
        label: type.name,
    }))

    const tabs: TabItem[] = [
        {
            label: 'Main Details',
            content: (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormInput name="name" label={formatMessage(messages.name)} required />
                    </Grid>
                    <Grid item xs={12}>
                        {/* TODO: would be easy add when requested  */}
                        {/* <FormInput
                            name="description"
                            label={formatMessage(messages.description)}
                            multiline
                            rows={4}
                        /> */}
                    </Grid>
                    <Grid item xs={12}>
                        <FormInput name="isoCode" label={formatMessage(messages.isoCode)} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormSelect
                            name="loadTypeId"
                            label={formatMessage(messages.loadType)}
                            options={loadTypeOptions}
                            required
                        />
                    </Grid>
                </Grid>
            ),
        },
        {
            label: 'Physical Properties',
            hasError: !!methods.formState.errors.physicalProperties,
            content: <SharedPhysicalProperties variant='loadTemplate' loadTypes={loadTypes} isVolume={true} />,
        },
    ]
    console.log('errors ->', methods.formState.errors)

    return (
        <SideModalWrapper
            open={isOpen}
            onClose={handleClose}
            headerLabel={formatMessage(messages.createEditLoadTemplate)}
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Box sx={{ minWidth: '450px', m: '32px' }}>
                        <TabWrapper tabs={tabs} />
                    </Box>
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            width: '100%',
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderTop: '1px solid #e0e0e0',
                            zIndex: 999,
                        }}
                    >
                        <SubmitButtons
                            create={isNew}
                            isEnabled={true}
                            onCancel={handleClose}
                            submitForm={methods.handleSubmit(onSubmit)}
                        />
                    </Box>
                </form>
            </FormProvider>
        </SideModalWrapper>
    )
}

export default LoadTemplateForm        